/**
 * @author liuzhao
 * @description 根据'@tiptap/extension-task-item' 改造task-item ： 支持中文【】激活任务列表
 */

import {
  KeyboardShortcutCommand, mergeAttributes, Node, wrappingInputRule, InputRule
} from '@tiptap/core'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'
import {
  imgPathPerfix,
  isValidYYYYMMDD,
  yyyymmddToNoonTimestamp,
  getDueDateValue,
  generateUUID2,
  sleep, getImagePathWithThemeSvg
} from "@/utils";
import { Plugin, PluginKey } from 'prosemirror-state';
import {
  TextSelection,
} from '@tiptap/pm/state';
import { joinForward } from 'prosemirror-commands';
import { DatePicker } from '@/components/DatePickerPop/DatePickerManager';
import { updateCard, getAllCardList } from "@model/card";
import { imgServer } from '@/components/EditorV2/imgServer'
import { postMessage, recvMessage, unSubscribe } from "@/common/event";
import {
  EVENT_UPDATE_NOTE,
} from "@/Const";
const dateSvg = `<svg t="1756597981062" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="4447" width="20" height="20"><path d="M231.424 272.384l540.672 0 0 59.392-540.672 0 0-59.392Z" p-id="4448"></path><path d="M606.208 202.752l43.008 0 0 118.784-43.008 0 0-118.784Z" p-id="4449"></path><path d="M354.304 202.752l43.008 0 0 118.784-43.008 0 0-118.784Z" p-id="4450"></path><path d="M231.424 372.736 231.424 798.72l540.672 0L772.096 372.736 231.424 372.736zM364.544 765.952l-106.496 0 0-106.496 106.496 0L364.544 765.952zM364.544 638.976l-106.496 0L258.048 532.48l106.496 0L364.544 638.976zM364.544 512l-106.496 0 0-106.496 106.496 0L364.544 512zM489.472 765.952l-106.496 0 0-106.496 106.496 0L489.472 765.952zM489.472 638.976l-106.496 0L382.976 532.48l106.496 0L489.472 638.976zM489.472 512l-106.496 0 0-106.496 106.496 0L489.472 512zM614.4 765.952l-106.496 0 0-106.496L614.4 659.456 614.4 765.952zM614.4 638.976l-106.496 0L507.904 532.48 614.4 532.48 614.4 638.976zM614.4 512l-106.496 0 0-106.496L614.4 405.504 614.4 512zM739.328 765.952l-106.496 0 0-106.496 106.496 0L739.328 765.952zM739.328 638.976l-106.496 0L632.832 532.48l106.496 0L739.328 638.976zM739.328 512l-106.496 0 0-106.496 106.496 0L739.328 512z" p-id="4451"></path></svg>`

export interface TaskItemOptions {
  /**
   * A callback function that is called when the checkbox is clicked while the editor is in readonly mode.
   * @param node The prosemirror node of the task item
   * @param checked The new checked state
   * @returns boolean
   */
  onReadOnlyChecked?: (node: ProseMirrorNode, checked: boolean) => boolean

  /**
   * Controls whether the task items can be nested or not.
   * @default false
   * @example true
   */
  nested: boolean

  /**
   * HTML attributes to add to the task item element.
   * @default {}
   * @example { class: 'foo' }
   */
  HTMLAttributes: Record<string, any>

  /**
   * The node type for taskList nodes
   * @default 'taskList'
   * @example 'myCustomTaskList'
   */
  taskListTypeName: string
}

/**
 * Matches a task item to a - [ ] on input.
 */
export const inputRegex = /^\s*([\[【]([( |x])?[\]】])\s$/

/**
 * This extension allows you to create task items.
 * @see https://www.tiptap.dev/api/nodes/task-item
 */
export const TaskItem = Node.create<TaskItemOptions>({
  name: 'taskItem',

  addOptions() {
    return {
      nested: false,
      HTMLAttributes: {},
      taskListTypeName: 'taskList',
    }
  },

  content() {
    return this.options.nested ? 'paragraph block*' : 'paragraph+'
  },

  defining: true,

  addAttributes() {
    return {
      checked: {
        default: false,
        keepOnSplit: false,
        parseHTML: element => {
          const dataChecked = element.getAttribute('data-checked')

          return dataChecked === '' || dataChecked === 'true'
        },
        renderHTML: attributes => ({
          'data-checked': attributes.checked,
        }),
      },
      id: {
        default: () => {
          return null
        },  // 生成唯一的 ID，只有第一次创建时生成
        parseHTML: element => {
          return element?.getAttribute('id') || crypto.randomUUID()
        },
      },
      listFold: {
        default: false,
        renderHTML: attributes => {
          if (attributes.listFold === undefined) {
            return {};
          }
          return {
            'data-listFold': attributes.listFold,
          };
        },
      },
      dueDate: {
        default: () => {
          const currentNoteName = sessionStorage.getItem('currentNoteName')
          let defaultDuedate
          if (currentNoteName && isValidYYYYMMDD(currentNoteName)) {
            defaultDuedate = yyyymmddToNoonTimestamp(currentNoteName)
          } else {
            defaultDuedate = 0
          }
          return defaultDuedate
        }, // 默认是0 表示没有到期时间
        keepOnSplit: false,
        parseHTML: element => element.getAttribute('data-dueDate') || 0,
        renderHTML: attributes => {
          return {
            'data-dueDate': attributes.dueDate,
          }
        },
      },
      // dueDate: {
      //   default: () => {
      //     const currentNoteName = sessionStorage.getItem('currentNoteName')
      //     let defaultDuedate
      //     if (currentNoteName && isValidYYYYMMDD(currentNoteName)) {
      //       defaultDuedate = yyyymmddToNoonTimestamp(currentNoteName)
      //     } else {
      //       defaultDuedate = 0
      //     }
      //     console.log('默认值计算:', defaultDuedate);
      //     return defaultDuedate
      //   },
      //   keepOnSplit: false,
      //   parseHTML: element => {
      //     const dueDateValue = element.getAttribute('data-dueDate');
      //     // 关键修改：只有当属性存在且不为空时才返回值，否则返回 undefined 让默认值生效
      //     if (dueDateValue && dueDateValue !== '' && dueDateValue !== 'null') {
      //       return parseInt(dueDateValue, 10);
      //     }
      //     return undefined; // 返回 undefined 而不是 0，这样会触发 default 函数
      //   },
      //   renderHTML: attributes => {
      //     return {
      //       'data-dueDate': attributes.dueDate,
      //     }
      //   },
      // }
      // ifhidden: {
      //   default: false,
      //   renderHTML: attributes => {
      //     if (attributes.ifhidden === undefined) {
      //       return {};
      //     }
      //     return {
      //       'ifhidden': attributes.ifhidden,
      //     };
      //   },
      // }
    }
  },

  parseHTML() {
    return [
      {
        tag: `li[data-type="${this.name}"]`,
        priority: 51,
        getAttrs: element => {
          // 必须显式解析属性
          const listFold = element.getAttribute('data-listFold') === 'true';
          return {
            listFold,
          };
        },
      },
    ]
  },

  renderHTML({ node, HTMLAttributes }) {
    const listFold = node.attrs.listFold || false; // 获取 fold 属性，默认为 false
    return [
      'li',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-type': this.name,
        class: 'task-container'
      }),
      [
        // 'img', // 使用 img 元素
        // {
        //   class: 'task-image', // 添加自定义样式类
        //   src: listFold ? imgPathPerfix() + "arrowRight.svg" : imgPathPerfix() + "arrowBottom.svg", // 设置 src 属性
        //   alt: 'task image', // 可设置 alt 文本描述
        // },
        'label',
        [
          'input',
          {
            type: 'checkbox',
            checked: node.attrs.checked ? 'checked' : null,
          },
        ],
        ['span'],
      ],
      ['div', 0],
    ]
  },

  addKeyboardShortcuts() {
    const shortcuts: {
      [key: string]: KeyboardShortcutCommand
    } = {
      'Enter': () => {
        const { state, view } = this.editor;
        const { selection } = state;
        const { from, to } = selection;
        const resolvedPos = this.editor.state.doc.resolve(from);
        // 获取当前节点
        const parentNode = resolvedPos.node(resolvedPos.depth - 1);
        if (parentNode.type.name === "taskItem" && !!document.querySelector('div[data-tippy-root]')) {
          return false
        }
        return this.editor.commands.splitListItem(this.name)
      },
      'Shift-Tab': () => this.editor.commands.liftListItem(this.name),
    }

    if (!this.options.nested) {
      return shortcuts
    }

    return {
      ...shortcuts,
      // Tab: () => {
      //   const { from } = this.editor.state.selection;
      //   const resolvedPos = this.editor.state.doc.resolve(from);
      //   const parentNode = resolvedPos.node(resolvedPos.depth - 1);
      //   if (parentNode.type.name === 'taskItem') {
      //     this.editor.commands.sinkListItem(this.name);
      //     return true
      //   } else {
      //     return false
      //   }
      // },
    }
  },

  addNodeView() {
    return ({
              node, HTMLAttributes, getPos, editor,
            }) => {
      const listItem = document.createElement('li')
      const checkboxWrapper = document.createElement('label')
      const checkboxStyler = document.createElement('span')
      const checkbox = document.createElement('input')
      const content = document.createElement('div')


      checkboxWrapper.contentEditable = 'false'
      checkbox.type = 'checkbox'
      checkbox.addEventListener('change', event => {
        // if the editor isn’t editable and we don't have a handler for
        // readonly checks we have to undo the latest change
        if (!editor.isEditable && !this.options.onReadOnlyChecked) {
          checkbox.checked = !checkbox.checked

          return
        }

        const { checked } = event.target as any

        if (editor.isEditable && typeof getPos === 'function') {
          editor
              .chain()
              .focus(undefined, { scrollIntoView: false })
              .command(({ tr }) => {
                const position = getPos()
                const currentNode = tr.doc.nodeAt(position)

                tr.setNodeMarkup(position, undefined, {
                  ...currentNode?.attrs,
                  checked,
                })

                return true
              })
              .run()
        }
        if (!editor.isEditable && this.options.onReadOnlyChecked) {
          // Reset state if onReadOnlyChecked returns false
          if (!this.options.onReadOnlyChecked(node, checked)) {
            checkbox.checked = !checkbox.checked
          }
        }
      })

      Object.entries(this.options.HTMLAttributes).forEach(([key, value]) => {
        listItem.setAttribute(key, value)
      })

      listItem.dataset.checked = node.attrs.checked
      if (node.attrs.checked) {
        checkbox.setAttribute('checked', 'checked')
      }

      checkboxWrapper.append(checkbox, checkboxStyler)
      // listItem.addEventListener('mouseover', (e) => {
      //   e.stopPropagation()
      //   console.log('鼠标经过：',crypto.randomUUID());
      // });
      // 添加fold icon
      const foldIcon = document.createElement('img')
      foldIcon.className = 'task-image'
      foldIcon.src = node.attrs.listFold
          ? getImagePathWithThemeSvg("arrowRight")
          : getImagePathWithThemeSvg("arrowBottom")
      foldIcon.contentEditable = 'false'  // 防止被编辑
      foldIcon.addEventListener('click', (e) => {
        e.stopPropagation(); // 阻止事件冒泡（可选）
        setTimeout(() => {
          node.attrs.listFold = !node.attrs.listFold;
          foldIcon.src = node.attrs.listFold
              ? getImagePathWithThemeSvg("arrowRight")
              : getImagePathWithThemeSvg("arrowBottom");
          // 获取 listItem 内的 ul 或 ol 元素
          const subList = listItem.querySelector('ul, ol');
          if (subList) {
            const { state, dispatch } = editor.view;
            const { tr } = state;
            const { schema } = state;
            // 通过tiptap api修改状态
            // subList.style.display = node.attrs.listFold ? 'none' : '';
            const selectNodeNP = editor.$doc.querySelectorAll('taskItem', { id: listItem.id })[0]
            selectNodeNP.children.forEach(ele => {
              if (ele.node.type.name === 'taskList' || ele.node.type.name === 'bulletList' || ele.node.type.name === 'orderedList') {
                tr.setNodeMarkup(ele.pos - 1, schema.nodes[`${ele.node.type.name}`], {
                  ...ele.node.attrs,
                  ifhidden: selectNodeNP.attributes.listFold ? true : false
                });
              }
            })
            // 修改当前节点的listfold
            tr.setNodeMarkup(selectNodeNP.pos - 1, schema.nodes[`${selectNodeNP.node.type.name}`], {
              ...selectNodeNP.node.attrs,
              listFold: node.attrs.listFold
            });
            dispatch(tr);
          }
        }, 20);
      });
      // 渲染时间到内容上
      const dueDate = document.createElement('span')
      dueDate.className = 'taskItem-dueData'
      // 点击跳出日期弹窗
      dueDate.addEventListener('mousedown', async (event) => {
        event.preventDefault()
        event.stopPropagation()
        try {
          // 在鼠标点击位置显示日历弹窗
          const selectNodeNP = editor.$doc.querySelectorAll('taskItem', { id: node.attrs.id })[0]
          const selectedDate = await DatePicker.open(event.pageX, event.pageY, (date: string) => { }, selectNodeNP.node.attrs.dueDate);
          let anotherFormatDate = selectedDate.replace(/-/g, "")
          const currentNoteName = sessionStorage.getItem('currentNoteName')
          if (currentNoteName && isValidYYYYMMDD(currentNoteName) && selectedDate !== '-1' && currentNoteName !== anotherFormatDate) {
            const selectNodeNP = editor.$doc.querySelectorAll('taskItem', { id: node.attrs.id })[0]
            const resolvedPos = editor.state.doc.resolve(selectNodeNP.from);

            // 针对需要移动todo的特殊逻辑
            // 判断目标卡片是否存在  没有就添加
            const allNote = await getAllCardList()
            let targetCard = allNote.find(card => card.name === anotherFormatDate)
            let existCard
            if (!targetCard) {
              // 先添加笔记
              existCard = await updateCard({
                id: generateUUID2(),
                doc: "<p ifhidden=\"false\"></p>",
                type: "card",
                name: anotherFormatDate,
              })
              await sleep(200)
            } else {
              existCard = targetCard;
            }
            {
              // 将元素添加到目标笔记末尾
              // 需要修改task的duedate!!!
              try {
                const year = parseInt(anotherFormatDate.slice(0, 4), 10);
                const month = parseInt(anotherFormatDate.slice(4, 6), 10) - 1; // JS月份从0开始
                const day = parseInt(anotherFormatDate.slice(6, 8), 10);
                const date = new Date(year, month, day, 12, 0, 0); // 中午12点
                const { state, view } = window.publicEditor;
                const { schema } = state;

                // 先设置内容
                window.publicEditor?.commands.setContent(existCard.doc);

                // 重新获取最新的状态
                const newState = view.state;
                const { doc, tr } = newState;

                const paragraph = schema.nodes.paragraph.create(null, schema.text(content.textContent));
                const task = schema.nodes.taskItem.create(
                    {
                      checked: false,
                      id: crypto.randomUUID(),
                      listFold: false,
                      dueDate: String(date.getTime()),
                    },
                    paragraph
                );

                const endPos = doc.content.size;
                const transaction = tr.insert(endPos, task);
                view.dispatch(transaction);
                // 保存
                const html = imgServer.parserImgFromHtml(window.publicEditor?.getHTML() as string);
                const name = existCard?.name;
                let createTime = existCard.createTime;
                let updateTime = Date.now();
                if (!createTime) {
                  createTime = updateTime;
                }
                const data = {
                  doc: html,
                  thumbHtml: html,
                  id: existCard.id,
                  type: 'card',
                  isDeleted: 0,
                  name: name || '',
                  createTime,
                  updateTime
                };
                postMessage(EVENT_UPDATE_NOTE, { data: data, isInsertCanvas: false, isAutoSave: true });
              } catch (error) {
              }
            }
            {
              // 将元素从原笔记中删除
              const { state, view } = editor;
              const { doc, tr } = state;
              const currentNode = resolvedPos.node();
              const parentNode = resolvedPos.node(1);
              const parentResolvedPos = state.doc.resolve(resolvedPos.start(resolvedPos.depth - 1));
              if (selectNodeNP.depth <= 3) {
                if (parentNode.type.name === "taskList" && parentNode.childCount === 1) {
                  // 直接把整个list移除
                  const from = parentResolvedPos.start(parentResolvedPos.depth);
                  const to = parentResolvedPos.end(parentResolvedPos.depth);
                  view.dispatch(tr.delete(from - 1, to));
                } else {
                  const from = resolvedPos.start(resolvedPos.depth);
                  const to = resolvedPos.end(resolvedPos.depth);
                  view.dispatch(tr.delete(from - 1, to));
                }
              } else {
                view.dispatch(tr.delete(selectNodeNP.from - 2, selectNodeNP.to));
              }
            }
          } else {
            const targetNode = editor.$doc.querySelectorAll('taskItem', {
              id: node.attrs.id
            })[0];
            let tr = editor.view.state.tr.setNodeMarkup(targetNode.pos - 1, editor.view.state.schema.nodes.taskItem, {
              ...node.attrs,
              dueDate: selectedDate === '-1' ? '-1' : new Date(`${selectedDate}T12:00:00`).getTime()
            });
            editor.view.dispatch(tr)
          }
        } catch (error) {
        }
      })
      if (Number(node.attrs.dueDate) > 0) {
        const date = new Date(Number(node.attrs.dueDate))
        // 格式化为 "May 16, 2025"
        const formattedDate = date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
        dueDate.textContent = formattedDate

        // 判断是否过期
        const now = new Date()
        now.setHours(0, 0, 0, 0)
        if (date < now) {
          dueDate.style.color = '#bb5141'   // 过期
        } else {
          dueDate.style.color = '#4c7ff7'  // 未过期
        }
        listItem.append(foldIcon, checkboxWrapper, content, dueDate)
      } else {
        dueDate.innerHTML = dateSvg
        listItem.append(foldIcon, checkboxWrapper, content, dueDate)
      }
      listItem.classList.add('task-container')

      Object.entries(HTMLAttributes).forEach(([key, value]) => {
        listItem.setAttribute(key, value)
      })

      return {
        dom: listItem,
        contentDOM: content,
        update: updatedNode => {
          if (updatedNode.type !== this.type) {
            return false
          }

          listItem.dataset.checked = updatedNode.attrs.checked
          if (updatedNode.attrs.checked) {
            checkbox.setAttribute('checked', 'checked')
          } else {
            checkbox.removeAttribute('checked')
          }

          // 添加对 dueDate 变化的处理 考虑到嵌套的结构
          // const existingDueDate = listItem.querySelector('.taskItem-dueData');
          const lastChild = listItem.lastElementChild;
          if (Number(updatedNode.attrs.dueDate) > 0) {
            const date = new Date(Number(updatedNode.attrs.dueDate));
            const formattedDate = date.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            });
            if (lastChild && lastChild.classList.contains('taskItem-dueData')) {
              lastChild.textContent = formattedDate;
              const now = new Date()
              now.setHours(0, 0, 0, 0)

              if (date < now) {
                dueDate.style.color = '#bb5141'   // 过期
              } else {
                dueDate.style.color = '#4c7ff7'  // 未过期
              }
              listItem.appendChild(dueDate);
            } else {
              const dueDate = document.createElement('span');
              dueDate.className = 'taskItem-dueData';
              dueDate.textContent = formattedDate;
              const now = new Date()
              now.setHours(0, 0, 0, 0)

              if (date < now) {
                dueDate.style.color = '#bb5141'   // 过期
              } else {
                dueDate.style.color = '#4c7ff7'  // 未过期
              }
              listItem.appendChild(dueDate);
            }
          } else if (Number(updatedNode.attrs.dueDate) === -1) {
            // 如果 dueDate 为 0 或空，移除显示的日期
            if (lastChild && lastChild.classList.contains('taskItem-dueData')) {
              // lastChild.remove();
              lastChild.innerHTML = dateSvg

            }
          }

          return true
        },
      }
    }
  },
  addProseMirrorPlugins() {
    return [
      // 确保每个新 taskitem 生成唯一 ID
      new Plugin({
        key: new PluginKey("taskItemIdPlugin"),
        appendTransaction: (transactions, oldState, newState) => {
          // 只处理有实际变更的事务
          if (!transactions.some(tr => tr.docChanged)) return null;

          let tr = newState.tr;
          let modified = false;

          // 记录所有已使用的ID
          const usedIds = new Set();

          newState.doc.descendants((node, pos) => {
            if (node.type.name === "taskItem") {
              if (!node.attrs.id) {
                // 节点没有ID，分配一个新ID
                const newId = crypto.randomUUID();
                tr = tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  id: newId,
                  dueDate: getDueDateValue()
                });
                usedIds.add(newId);
                modified = true;
                console.log("分配新ID:", newId, "给位置:", pos);
              } else if (usedIds.has(node.attrs.id)) {
                // ID已经被使用，需要重新分配
                const newId = crypto.randomUUID();
                tr = tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  id: newId,
                  dueDate: getDueDateValue()
                });
                usedIds.add(newId);
                modified = true;
                console.log("重新分配ID:", node.attrs.id, "->", newId, "给位置:", pos);
              } else {
                // 记录当前ID为已使用
                usedIds.add(node.attrs.id);
              }
            }
          })
          return modified ? tr : null;
        }
      }),
    ];
  },

  addInputRules() {
    const wrapping = wrappingInputRule({
      find: inputRegex,
      type: this.type,
      getAttributes: match => ({
        checked: match[1].includes('x'),
      }),
    })

    const customRule = new InputRule({
      find: inputRegex,
      handler: ({ state, range, match, chain }) => {
        const resolvedPos = this.editor.state.doc.resolve(range.from)
        const nodeStart = resolvedPos.start(resolvedPos.depth)
        const nodeEnd = resolvedPos.end(resolvedPos.depth)

        const currentNode = resolvedPos.node()
        const parentNode = resolvedPos.node(resolvedPos.depth - 1)

        let lastNodeResolved;
        let nextNodeResolved;
        try {
          lastNodeResolved = this.editor.state.doc.resolve(nodeStart - 2);
        } catch (error) {
          lastNodeResolved = undefined
        }
        try {
          nextNodeResolved = this.editor.state.doc.resolve(nodeEnd + 2);
        } catch (error) {
          nextNodeResolved = undefined
        }
        const lastNode = lastNodeResolved?.node();
        const nextNode = nextNodeResolved?.node();
        // 合并两个list
        if (lastNode?.type.name === 'taskList' && nextNode?.type.name === 'taskList') {
          setTimeout(() => {
            this.editor.chain().focus().command(({ state, dispatch }) => {
              return joinForward(state, dispatch);
            }).run();
          }, 50);
        }
        const { schema } = state
        const tr = state.tr

        // 特殊结构处理：缩进嵌套
        if (
            currentNode.type.name === 'paragraph' &&
            currentNode.attrs.indent >= 1 &&
            lastNodeResolved &&
            ['bulletList', 'orderedList', 'taskList'].includes(lastNode?.type.name)
        ) {
          const isChecked = match[1].includes('x')
          const uuid = crypto.randomUUID()
          const listItem = schema.nodes.taskItem.create({ checked: isChecked }, schema.nodes.paragraph.create({ id: uuid }))
          const taskList = schema.nodes.taskList.create({}, [listItem])

          const lastNodeJson = lastNode.toJSON()

          const lastNodeStart = lastNodeResolved.start(lastNodeResolved.depth)

          const firstLevelLastListItem = lastNodeJson.content[lastNodeJson.content.length - 1]
          firstLevelLastListItem.content.push(taskList.toJSON())
          lastNodeJson.content[lastNodeJson.content.length - 1] = firstLevelLastListItem

          tr.replaceRangeWith(
              lastNodeStart,
              nodeEnd,
              schema.nodeFromJSON(lastNodeJson)
          )
          this.editor.view.dom.blur();
          this.editor.view.dispatch(tr);
          setTimeout(() => {
            const selectNodeNP = this.editor.$doc.querySelectorAll('paragraph', { id: uuid })[0]
            this.editor.commands.focus(selectNodeNP.from)
          }, 10);
          return true
        }

        // 否则走默认 wrappingInputRule
        return wrapping.handler({ state, range, match, chain })
      },
    })

    return [customRule]
  },
})

export default TaskItem
